import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Link } from 'react-router-dom';
import { cn } from '@/lib/utils';

interface DocsMarkdownProps {
  content: string;
  className?: string;
}

const remarkPlugins = [remarkGfm];

const docsComponents = {
  h1: ({ className, ...props }: any) => (
    <h1 className={cn("text-2xl font-bold mb-4 first:mt-0 mt-8 pb-2 border-b border-border", className)} {...props} />
  ),
  h2: ({ className, ...props }: any) => (
    <h2 className={cn("text-xl font-bold mb-3 first:mt-0 mt-7 pb-1 border-b border-border", className)} {...props} />
  ),
  h3: ({ className, ...props }: any) => (
    <h3 className={cn("text-lg font-semibold mb-2 first:mt-0 mt-6", className)} {...props} />
  ),
  h4: ({ className, ...props }: any) => (
    <h4 className={cn("text-base font-semibold mb-2 first:mt-0 mt-4", className)} {...props} />
  ),
  p: ({ className, children, ...props }: any) => {
    // Check if this paragraph contains only an image placeholder
    if (typeof children === 'string' && children.startsWith('[Image:')) {
      return null;
    }
    return <p className={cn("mb-4 last:mb-0 leading-7", className)} {...props}>{children}</p>;
  },
  a: ({ className, href, children, ...props }: any) => {
    const linkClass = cn("text-primary underline underline-offset-4 hover:text-primary/80", className);
    if (href?.startsWith('/docs/')) {
      return <Link to={href} className={linkClass}>{children}</Link>;
    }
    return <a className={linkClass} href={href} {...props}>{children}</a>;
  },
  blockquote: ({ className, children, ...props }: any) => {
    // Detect tip/note callouts from the content
    const text = extractText(children);
    const isTip = text.startsWith('Pliable Tip:') || text.startsWith('Tip:') || text.startsWith('💡');
    const isNote = text.startsWith('a note from') || text.startsWith('📎');
    const isHint = text.startsWith('Hint:');

    if (isTip || isHint) {
      return (
        <div className={cn("my-4 rounded-lg border border-primary/30 bg-primary/5 p-4", className)} {...props}>
          <div className="flex gap-2">
            <span className="text-primary font-medium shrink-0">💡 Tip</span>
            <div className="[&>p]:mb-0">{children}</div>
          </div>
        </div>
      );
    }
    if (isNote) {
      return (
        <div className={cn("my-4 rounded-lg border border-info/30 bg-info/5 p-4", className)} {...props}>
          <div className="flex gap-2">
            <span className="text-info font-medium shrink-0">📎 Note</span>
            <div className="[&>p]:mb-0">{children}</div>
          </div>
        </div>
      );
    }

    return (
      <blockquote className={cn("border-l-4 border-border pl-4 italic my-4", className)} {...props}>
        {children}
      </blockquote>
    );
  },
  ul: ({ className, ...props }: any) => (
    <ul className={cn("list-disc mb-4 space-y-2 pl-6", className)} {...props} />
  ),
  ol: ({ className, ...props }: any) => (
    <ol className={cn("list-decimal mb-4 space-y-2 pl-6", className)} {...props} />
  ),
  li: ({ className, ...props }: any) => (
    <li className={cn("pl-1 leading-7", className)} {...props} />
  ),
  hr: ({ className, ...props }: any) => (
    <hr className={cn("my-8 border-border", className)} {...props} />
  ),
  table: ({ className, ...props }: any) => (
    <div className="my-4 overflow-x-auto">
      <table className={cn("w-full border-collapse border border-border", className)} {...props} />
    </div>
  ),
  th: ({ className, ...props }: any) => (
    <th className={cn("border border-border px-4 py-2 bg-muted font-semibold text-left", className)} {...props} />
  ),
  td: ({ className, ...props }: any) => (
    <td className={cn("border border-border px-4 py-2", className)} {...props} />
  ),
  tr: ({ className, ...props }: any) => (
    <tr className={cn("", className)} {...props} />
  ),
  code: ({ className, inline, ...props }: any) => {
    if (inline) {
      return <code className={cn("bg-muted px-1.5 py-0.5 rounded text-sm font-mono", className)} {...props} />;
    }
    return <code className={cn("text-sm font-mono", className)} {...props} />;
  },
  pre: ({ className, ...props }: any) => (
    <pre className={cn("bg-muted p-4 rounded-lg overflow-x-auto my-4 [&>code]:block [&>code]:p-0 [&>code]:bg-transparent", className)} {...props} />
  ),
  img: ({ src, alt, className, ...props }: any) => {
    // Skip Notion attachment images that won't load
    if (src?.includes('attachment:') || src?.includes('prod-files-secure.s3')) {
      return null;
    }
    return <img src={src} alt={alt} className={cn("rounded-lg border max-w-full my-4", className)} {...props} />;
  },
};

function extractText(children: React.ReactNode): string {
  if (typeof children === 'string') return children;
  if (Array.isArray(children)) {
    return children.map(extractText).join('');
  }
  if (children && typeof children === 'object' && 'props' in (children as any)) {
    return extractText((children as any).props.children);
  }
  return '';
}

export const DocsMarkdown = React.memo<DocsMarkdownProps>(({ content, className }) => {
  // Clean up Notion artifacts from content
  const cleaned = content
    .replace(/\[Sub-page:.*?\]/g, '')
    .replace(/\[Image: attachment:.*?\]/g, '')
    .replace(/\[Image: https:\/\/prod-files-secure\.s3.*?\]/g, '')
    .trim();

  return (
    <div className={cn("max-w-none", className)}>
      <ReactMarkdown remarkPlugins={remarkPlugins} components={docsComponents}>
        {cleaned}
      </ReactMarkdown>
    </div>
  );
});
DocsMarkdown.displayName = 'DocsMarkdown';
